import { useState } from "react";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { keepPreviousData } from "@tanstack/react-query";
import { createWorkOrderJobOpeningMutation } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderJobOpeningMutations";
import { proDriverCountQuery } from "@/app/ProDrivers/Drivers/GraphQL/proDriverCountQuery";
import { CreateWorkOrderJobOpeningInput } from "@/gql/graphql";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { StepperInput } from "@/lib/Components/Form/Inputs/StepperInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { useTranslations } from "use-intl";
import { useWorkOrder } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrder";
import { ProDriverTable } from "@/app/ProDrivers/Drivers/Components/ProDriverTable";
import { WorkOrderAdminNotes } from "@/app/ProDrivers/WorkOrders/Components/WorkOrderAdminNotes";
import { Circle, Marker, Popup } from "react-leaflet";
import { Map } from "@/components/ui/map";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png";
import L, { LatLngTuple } from "leaflet";
import { Slider } from "@/components/ui/slider";
import "leaflet/dist/leaflet.css";
import { BasemapLayer } from "@/components/map/BasemapLayer";

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconRetinaUrl: iconRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface CreateWorkOrderJobOpeningDialogProps {
  workOrderId: string;
  proDriverIds?: string[];
}

export function CreateWorkOrderJobOpeningDialog({
  workOrderId,
  proDriverIds,
}: CreateWorkOrderJobOpeningDialogProps) {
  const t = useTranslations("workOrder.jobOpenings.createDialog");
  const { close, resolve } = useDialogState();
  const { mutateAsync } = useGqlMutation(createWorkOrderJobOpeningMutation);
  const workOrder = useWorkOrder(workOrderId);
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>(
    proDriverIds ?? [],
  );
  const [mode, setMode] = useState<"all" | "specific">(
    proDriverIds?.length ? "specific" : "all",
  );
  const tMap = useTranslations("workOrder.driversScreen");
  const tDetails = useTranslations("workOrder.details");
  const { departureAddress, deliveryAddress } = workOrder;
  const [locationMode, setLocationMode] = useState<"departure" | "delivery">(
    "departure",
  );
  const address =
    locationMode === "departure" ? departureAddress : deliveryAddress;
  const center = [address.lat, address.lng] as LatLngTuple;
  const [radius, setRadius] = useState(100);

  const radiusAround = { radius, lat: center[0], lng: center[1] };
  const { data: countData } = useGqlQuery(
    proDriverCountQuery,
    {
      radiusAround,
      archived: false,
    },
    { placeholderData: keepPreviousData },
  );
  const driversInArea = countData?.proDrivers.paginatorInfo.total ?? 0;

  const validation = z.object({
    description: z
      .string({ error: t("descriptionRequired") })
      .min(1, t("descriptionRequired")),
    positions_available: z.number().min(1, t("positionsRequired")),
    work_order_id: z.string(),
  });

  return (
    <BaseDialog title={t("title")} subtitle={t("subtitle")} size="5xl">
      <GenericForm<CreateWorkOrderJobOpeningInput>
        initialValues={{
          description: workOrder.description ?? "",
          positions_available: workOrder.number_of_drivers ?? 1,
          depart_at: workOrder.depart_after ?? null,
          deliver_at: workOrder.deliver_before ?? null,
          work_order_id: workOrderId,
        }}
        validationSchema={toFormikValidationSchema(validation)}
        onSubmit={async (values) => {
          await mutateAsync({
            input: {
              ...values,
              ...(mode === "all"
                ? { driver_filter: { radiusAround, archived: false } }
                : { pro_driver_ids: selectedDriverIds }),
            },
          });

          await close();
          resolve?.({});
        }}
      >
        <DateInput
          name="depart_at"
          label={t("departAt")}
          className="col-span-3"
          optionalLabel
        />

        <DateInput
          name="deliver_at"
          label={t("deliverAt")}
          className="col-span-3"
          optionalLabel
        />

        <StepperInput
          name="positions_available"
          label={t("positionsAvailable")}
          className="col-span-3"
        />

        <TextAreaInput
          name="description"
          label={t("description")}
          className="col-span-full"
          autoFocus
        />

        <div className="col-span-full">
          <h3 className="mb-2 text-sm font-medium text-gray-700">
            {tDetails("adminNotes")}
          </h3>
          <WorkOrderAdminNotes
            workOrderId={workOrderId}
            adminNotes={workOrder.admin_notes}
          />
        </div>

        <div className="col-span-full">
          <h3 className="mb-2 text-sm font-medium text-gray-700">
            {t("selectDrivers")}
          </h3>

          <div className="relative mb-4 overflow-hidden rounded-lg border shadow-lg">
            <Map
              key={locationMode}
              center={center}
              zoom={5}
              scrollWheelZoom={false}
              height="300px"
            >
              <BasemapLayer />
              <Marker position={center}>
                <Popup>
                  <div className="text-center">
                    <strong>{tMap("searchCenter")}</strong>
                    <br />
                    {tMap("lookingForDrivers", { radius })}
                  </div>
                </Popup>
              </Marker>
              <Circle
                center={center}
                radius={radius * 1000}
                pathOptions={{
                  fillColor: "#3b82f6",
                  fillOpacity: 0.1,
                  color: "#3b82f6",
                  weight: 2,
                  opacity: 0.6,
                }}
              />
            </Map>

            <div className="absolute right-4 top-4 z-[1000] w-64 rounded-lg border bg-white/95 p-4 shadow-lg backdrop-blur-sm">
              <div className="space-y-3">
                <div className="flex rounded-md bg-gray-100 p-0.5">
                  <button
                    type="button"
                    onClick={() => setLocationMode("departure")}
                    className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                      locationMode === "departure"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tDetails("pickUpLocation")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLocationMode("delivery")}
                    className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                      locationMode === "delivery"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tDetails("dropOffLocation")}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    {tMap("searchRadius")}
                  </label>
                  <span className="text-sm text-gray-500">{radius} km</span>
                </div>
                <Slider
                  value={[radius]}
                  onValueChange={(value) => setRadius(value[0])}
                  max={500}
                  min={50}
                  step={25}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-400">
                  <span>50km</span>
                  <span>500km</span>
                </div>

                <div className="flex rounded-md bg-gray-100 p-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      setMode("all");
                      setSelectedDriverIds([]);
                    }}
                    className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                      mode === "all"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {t("inviteAllDrivers", { count: driversInArea })}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("specific")}
                    className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                      mode === "specific"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {t("selectSpecificDrivers")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {mode === "specific" ? (
            <ProDriverTable
              id="create-job-opening-drivers"
              showActions={false}
              queryVariables={{ radiusAround }}
              selection={{
                selectedIds: selectedDriverIds,
                onChange: setSelectedDriverIds,
              }}
            />
          ) : null}
        </div>

        <DialogSubmitButton text={t("title")} />
      </GenericForm>
    </BaseDialog>
  );
}
